import { Crop, Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';

import { uploadMedia, publicURL as mediaPublicURL } from '../api/media';
import { ApiError } from '../api/client';
import { ImageCropDialog } from './ImageCropDialog';

// ImageInput is the reusable upload widget for any place we store a
// media object_key (custom-page image blocks, hero image overrides,
// gallery cells, etc.). Provides: thumbnail preview, choose-file
// button, remove button, error display, busy state.
//
// The form callers store the resulting object_key in their state and
// pass it back as `value`; this component is presentational + IO and
// holds no stable state of its own beyond busy/error.
//
// Variant `compact` shows a small square button suitable for inline
// use in a gallery grid; default is the larger card layout used by
// page-level image blocks and the hero override field.

export interface ImageInputProps {
  value: string;
  onChange: (objectKey: string) => void;
  variant?: 'default' | 'compact';
  // Optional override of the accept list. Defaults to PNG/JPEG/WebP.
  // SVG is intentionally excluded for content blocks (XSS risk on
  // inline SVG); the org logo upload path is the carve-out where SVG
  // is allowed because the renderer always sets <img src=…> there.
  accept?: string;
  // Aria label for the choose-file control. Localise upstream.
  label?: string;
  // When set, the Crop button is enabled and the dialog applies this
  // aspect ratio. Hero images typically want 16/9, square avatars
  // want 1, free-form omits this to render a "Free" cropper.
  cropAspect?: number;
  // upload defaults to the generic /api/v1/.../media uploader. Pass
  // a custom function when the caller's domain has its own upload
  // endpoint (broadcasts/upload-image, member photos, …) — same
  // contract: take a File, return an object that has object_key.
  upload?: (file: File) => Promise<{ object_key: string }>;
  // When true, hide the remove button — used when the image is
  // required and clearing it would put the parent record in an
  // invalid state (e.g. a partner card without a logo). The user
  // can still replace the image; they just can't blank it out.
  hideRemove?: boolean;
  // fallbackURL is shown as a preview when `value` is empty — useful
  // when the parent record's renderer falls back to a default image
  // (e.g. the public site's hero stock photo). The empty state then
  // reads as "this is what visitors currently see; click to replace"
  // instead of "no image". Ignored when value is non-empty.
  fallbackURL?: string;
  // fallbackLabel is the small caption shown over the fallback preview
  // so the user knows it isn't their own upload. Caller-localised.
  fallbackLabel?: string;
  // stockUrls maps "stock.<kind>[.<n>]" references to their resolved
  // CDN URLs. Used by the page-block editor: the AI page generator
  // populates image blocks with stock references; without this map the
  // ImageInput would try to render them via mediaPublicURL and end up
  // with broken /storage/stock.facility paths. Caller-resolved so the
  // component stays generic — non-stock-aware contexts (hero, partner
  // logos, gallery cells) omit the prop and behave exactly as before.
  stockUrls?: Record<string, string>;
}

export function ImageInput({
  value,
  onChange,
  variant = 'default',
  accept = 'image/png,image/jpeg,image/webp',
  label,
  cropAspect,
  upload,
  hideRemove = false,
  fallbackURL,
  fallbackLabel,
  stockUrls,
}: ImageInputProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const { slug = '' } = useParams<{ slug: string }>();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);

  async function onFile(file: File) {
    setBusy(true);
    setErr(null);
    try {
      const token = auth.user?.access_token ?? null;
      const result = upload
        ? await upload(file)
        : await uploadMedia(token, slug, file);
      onChange(result.object_key);
    } catch (e) {
      if (e instanceof ApiError) {
        setErr(e.code);
      } else {
        setErr('upload_failed');
      }
    } finally {
      setBusy(false);
    }
  }

  // Drag-and-drop handlers. Only fire for the empty state; replacing
  // an existing image still requires an explicit click so a stray
  // drag doesn't surprise-overwrite. Accept the first image-type file.
  const onDragOver = (ev: React.DragEvent) => {
    if (busy) return;
    ev.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (ev: React.DragEvent) => {
    if (busy) return;
    ev.preventDefault();
    setDragOver(false);
    const file = Array.from(ev.dataTransfer.files).find((f) => f.type.startsWith('image/'));
    if (file) void onFile(file);
  };

  const chooseLabel = label ?? t('imageInput.choose');

  // Resolve the preview URL for `value`. Stock references like
  // "stock.facility" or "stock.training.2" come from the AI page
  // generator — mediaPublicURL would turn them into broken
  // /storage/stock.facility paths. The stockUrls map (caller-provided)
  // carries the pre-resolved CDN URL. Falls back to mediaPublicURL for
  // ordinary admin-uploaded object keys.
  const isStock = value.startsWith('stock.');
  const previewURL = isStock && stockUrls?.[value] ? stockUrls[value] : mediaPublicURL(value);

  if (variant === 'compact') {
    return (
      <div className="image-input image-input--compact">
        {value ? (
          <span className="image-input__compact-preview">
            <img src={previewURL} alt="" />
            {!hideRemove && (
              <button
                type="button"
                aria-label={t('imageInput.remove')}
                className="image-input__remove"
                onClick={() => onChange('')}
              >×</button>
            )}
          </span>
        ) : (
          <label className="image-input__compact-empty">
            {busy ? '…' : '+'}
            <input
              type="file"
              accept={accept}
              hidden
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
                e.target.value = '';
              }}
            />
          </label>
        )}
        {err && <span className="image-input__error" role="alert">{t(`imageInput.error.${err}`, { defaultValue: t('imageInput.error.unknown') })}</span>}
      </div>
    );
  }

  return (
    <div className="image-input">
      {value ? (
        <div className="image-input__preview">
          <img src={previewURL} alt="" />
          {busy && (
            <span className="image-input__busy">{t('common.submitting')}</span>
          )}
          <span className="image-input__actions">
            {/* Crop only makes sense for assets the org owns — stock
                references are CDN photos we can't transform. */}
            {!isStock && (
              <button
                type="button"
                className="image-input__action-btn"
                aria-label={t('imageInput.crop')}
                title={t('imageInput.crop')}
                onClick={() => setCropOpen(true)}
              >
                <Crop size={20} aria-hidden />
              </button>
            )}
            {!hideRemove && (
              <button
                type="button"
                className="image-input__action-btn image-input__action-btn--danger"
                aria-label={t('imageInput.remove')}
                title={t('imageInput.remove')}
                onClick={() => onChange('')}
              >
                <Trash size={20} aria-hidden />
              </button>
            )}
          </span>
        </div>
      ) : fallbackURL ? (
        <div
          className={`image-input__preview image-input__preview--fallback${dragOver ? ' image-input__empty--drag' : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <img src={fallbackURL} alt="" />
          <span className="image-input__fallback-label">
            {dragOver
              ? t('imageInput.dropHere')
              : fallbackLabel ?? t('imageInput.fallback')}
          </span>
        </div>
      ) : (
        <div
          className={`image-input__empty${dragOver ? ' image-input__empty--drag' : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {dragOver ? t('imageInput.dropHere') : t('imageInput.empty')}
        </div>
      )}
      {value && !isStock && (
        <ImageCropDialog
          open={cropOpen}
          src={previewURL}
          filename={value}
          {...(cropAspect !== undefined ? { aspect: cropAspect } : {})}
          onCrop={async (file) => {
            const token = auth.user?.access_token ?? null;
            const result = upload
              ? await upload(file)
              : await uploadMedia(token, slug, file);
            onChange(result.object_key);
          }}
          onClose={() => setCropOpen(false)}
        />
      )}
      {/* No image yet: the choose-file button is the only add affordance.
          Once an image exists, removing it (trashcan) returns to this
          state, so a dedicated replace button is redundant. */}
      {!value && (
        <label className="btn btn--ghost image-input__upload">
          {busy ? t('common.submitting') : chooseLabel}
          <input
            type="file"
            accept={accept}
            hidden
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              e.target.value = '';
            }}
          />
        </label>
      )}
      {err && (
        <p className="image-input__error" role="alert">
          {t(`imageInput.error.${err}`, { defaultValue: t('imageInput.error.unknown') })}
        </p>
      )}
    </div>
  );
}
